// CommunityDetailPage.tsx — public community detail over the A4 shell.
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CommunityWelcome } from '@/features/networks/CommunityWelcome';
import { useCommunity, useSession } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { GuestBanner } from '@/components/GuestBanner';
import { TrendingPanel } from '@/components/panels/TrendingPanel';
import { CommunityBanner } from './CommunityBanner';
import { CommunitySubTabs } from './CommunitySubTabs';
import { CommunityFeed } from './CommunityFeed';
import { AboutTab } from './AboutTab';
import { EventsTab } from './EventsTab';
import { RulesTab } from './RulesTab';
import { MediaTab } from './MediaTab';
import { MembersComingSoon } from './MembersComingSoon';
import { CommunityNotFound } from './CommunityNotFound';
import { AboutCommunityPanel } from './panels/AboutCommunityPanel';
import { CommunityRulesPanel } from './panels/CommunityRulesPanel';
import { ModeratorsPanel } from './panels/ModeratorsPanel';
import { UpcomingEventsPanel } from './panels/UpcomingEventsPanel';
import type { SubTab } from './meta';

export function CommunityDetailPage() {
  const { id } = useParams();
  const { community, loading } = useCommunity(id);
  const { isAuthenticated } = useSession();
  const [subTab, setSubTab] = useState<SubTab>('posts');

  const rightRail = useMemo(
    () =>
      community ? (
        <RailGroup pageKey="community-detail">
          <RailPanel id="about" label="About the community">
            <AboutCommunityPanel community={community} />
          </RailPanel>
          <RailPanel id="rules" label="Community rules">
            <CommunityRulesPanel community={community} />
          </RailPanel>
          <RailPanel id="moderators" label="Moderators">
            <ModeratorsPanel community={community} />
          </RailPanel>
          <RailPanel id="events" label="Upcoming events">
            <UpcomingEventsPanel community={community} />
          </RailPanel>
          <RailPanel id="trending" label="Trending">
            <TrendingPanel />
          </RailPanel>
        </RailGroup>
      ) : null,
    [community],
  );
  useRightRail(rightRail);

  // Hold the frame while the by-id lookup is in flight — rendering
  // CommunityNotFound here would flash it for every real community.
  if (loading) return null;
  if (!community) return <CommunityNotFound />;

  return (
    <section>
      {!isAuthenticated && <GuestBanner />}
      <CommunityBanner community={community} onCreatePost={() => setSubTab('posts')} />
      <CommunityWelcome slug={community.id} />
      <CommunitySubTabs active={subTab} onChange={setSubTab} />
      {subTab === 'posts' && <CommunityFeed community={community} />}
      {subTab === 'about' && <AboutTab community={community} />}
      {subTab === 'members' && <MembersComingSoon />}
      {subTab === 'media' && <MediaTab community={community} />}
      {subTab === 'events' && <EventsTab community={community} />}
      {subTab === 'rules' && <RulesTab community={community} />}
    </section>
  );
}
